/**
 * 全息 3D 投影效果（Holographic 3D Projection）
 * 
 * 设计灵感：
 * - 科幻电影中的全息投影（星球大战、钢铁侠）
 * - 2026 AR/VR 趋势 - 全息界面成为主流
 * - 增强现实设备的可视化需求
 * 
 * 核心技术：
 * - 3D 几何体旋转（立方体 + 球体 + 八面体）
 * - 全息扫描线（水平扫描动画）
 * - 发光轮廓效果（多层 glow + blur）
 * - 粒子环绕系统（轨道运动）
 * - 底部投影光锥（锥形渐变）
 * - 确定性随机（帧级一致性）
 * 
 * 应用场景：
 * - 科技产品发布会
 * - AR/VR 设备宣传
 * - 未来主题视频
 * - 科幻风格内容
 * - 小红书科技感视频
 */

import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  random,
} from 'remotion';

// ============================================
// 配置常量
// ============================================

// 颜色配置
const COLORS = {
  primary: '#00D9FF',      // 青色（全息主色）
  secondary: '#0099FF',    // 蓝色
  accent: '#00FFD9',       // 青绿色
  glow: 'rgba(0, 217, 255, 0.5)',
  background: '#0A0E1A',   // 深蓝黑
  scanline: 'rgba(0, 217, 255, 0.3)',
};

// 粒子配置
const PARTICLE_COUNT = 30;

// ============================================
// 类型定义
// ============================================

interface Particle {
  x: number;
  y: number;
  z: number;
  angle: number;
  radius: number;
  speed: number;
  size: number;
  opacity: number;
}

// ============================================
// 工具函数
// ============================================

/**
 * 确定性随机函数（基于帧数）
 */
function seededRandom(seed: number): number {
  const x = Math.sin(seed * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

/**
 * 生成 3D 旋转的立方体路径
 */
function generateCubePath(rotation: number): string {
  const size = 80;
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);

  // 立方体的 8 个顶点（等轴测投影）
  const points = [
    { x: -size, y: -size, z: -size },
    { x: size, y: -size, z: -size },
    { x: size, y: size, z: -size },
    { x: -size, y: size, z: -size },
    { x: -size, y: -size, z: size },
    { x: size, y: -size, z: size },
    { x: size, y: size, z: size },
    { x: -size, y: size, z: size },
  ];

  // 旋转并投影到 2D
  const projected = points.map(p => {
    const x = p.x * cos - p.z * sin;
    const z = p.x * sin + p.z * cos;
    const scale = 1 / (1 - z / 500);
    return {
      x: x * scale + 540,
      y: p.y * scale + 960,
    };
  });

  // 绘制边缘
  const edges = [
    [0, 1], [1, 2], [2, 3], [3, 0], // 前面
    [4, 5], [5, 6], [6, 7], [7, 4], // 后面
    [0, 4], [1, 5], [2, 6], [3, 7], // 连接线
  ];

  return edges.map((edge, i) => {
    const start = projected[edge[0]];
    const end = projected[edge[1]];
    return `M ${start.x} ${start.y} L ${end.x} ${end.y}`;
  }).join(' ');
}

/**
 * 生成粒子系统
 */
function generateParticles(frame: number): Particle[] {
  const particles: Particle[] = [];

  for (let i = 0; i < PARTICLE_COUNT; i++) {
    const seed = i + 1;
    const baseAngle = seededRandom(seed) * Math.PI * 2;
    const radius = 150 + seededRandom(seed + 100) * 100;
    const speed = 0.02 + seededRandom(seed + 200) * 0.03;
    const angle = baseAngle + frame * speed;

    particles.push({
      x: Math.cos(angle) * radius,
      y: Math.sin(angle) * radius * 0.3, // 椭圆轨道
      z: Math.sin(angle * 2) * 50,
      angle,
      radius,
      speed,
      size: 2 + seededRandom(seed + 300) * 3,
      opacity: 0.3 + seededRandom(seed + 400) * 0.7,
    });
  }

  return particles;
}

// ============================================
// 主组件
// ============================================

export const Holographic3DProjection: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // ========================================
  // 动画参数
  // ========================================

  // 立方体旋转
  const cubeRotation = interpolate(frame, [0, fps * 5], [0, Math.PI * 2]);

  // 扫描线位置（上下循环）
  const scanlineY = interpolate(
    frame % (fps * 2),
    [0, fps * 2],
    [600, 1320]
  );

  // 整体透明度入场
  const opacity = spring({
    frame,
    fps,
    config: { damping: 200 },
  });

  // 缩放入场
  const scale = spring({
    frame,
    fps,
    config: { damping: 15, stiffness: 100 },
  });

  // 标题入场
  const titleY = spring({
    frame: frame - 10,
    fps,
    config: { damping: 12 },
  });

  // 生成粒子
  const particles = generateParticles(frame);

  return (
    <AbsoluteFill style={{ background: COLORS.background }}>
      {/* ================================== */}
      {/* 背景网格 */}
      {/* ================================== */}
      <svg
        width="100%"
        height="100%"
        style={{ position: 'absolute', opacity: 0.1 }}
      >
        <defs>
          <pattern
            id="grid"
            width="40"
            height="40"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M 40 0 L 0 0 0 40"
              fill="none"
              stroke={COLORS.primary}
              strokeWidth="0.5"
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" />
      </svg>

      {/* ================================== */}
      {/* 底部投影光锥 */}
      {/* ================================== */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '60%',
          transform: `translate(-50%, 0) scale(${scale})`,
          width: 400,
          height: 600,
          background: `conic-gradient(from 180deg at 50% 0%, transparent, ${COLORS.glow}, transparent)`,
          opacity: 0.3 * opacity,
          filter: 'blur(30px)',
        }}
      />

      {/* ================================== */}
      {/* 3D 立方体（全息效果） */}
      {/* ================================== */}
      <svg
        width="100%"
        height="100%"
        style={{
          position: 'absolute',
          opacity,
          transform: `scale(${scale})`,
        }}
      >
        <defs>
          {/* 发光滤镜 */}
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="4" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* 扫描线滤镜 */}
          <filter id="scanline-glow">
            <feGaussianBlur stdDeviation="8" />
          </filter>
        </defs>

        {/* 外层立方体（主轮廓） */}
        <path
          d={generateCubePath(cubeRotation)}
          fill="none"
          stroke={COLORS.primary}
          strokeWidth="2"
          filter="url(#glow)"
          opacity={0.8}
        />

        {/* 内层立方体（次轮廓） */}
        <path
          d={generateCubePath(cubeRotation + Math.PI / 4)}
          fill="none"
          stroke={COLORS.secondary}
          strokeWidth="1"
          filter="url(#glow)"
          opacity={0.4}
        />

        {/* 最外层立方体（装饰） */}
        <path
          d={generateCubePath(cubeRotation + Math.PI / 2)}
          fill="none"
          stroke={COLORS.accent}
          strokeWidth="0.5"
          filter="url(#glow)"
          opacity={0.2}
        />

        {/* 扫描线（水平移动） */}
        <line
          x1="0"
          y1={scanlineY}
          x2="1080"
          y2={scanlineY}
          stroke={COLORS.scanline}
          strokeWidth="2"
          filter="url(#scanline-glow)"
        />

        {/* 粒子系统 */}
        {particles.map((particle, i) => (
          <circle
            key={i}
            cx={540 + particle.x}
            cy={960 + particle.y}
            r={particle.size}
            fill={COLORS.primary}
            opacity={particle.opacity * opacity}
            filter="url(#glow)"
          />
        ))}
      </svg>

      {/* ================================== */}
      {/* 中心球体（全息球） */}
      {/* ================================== */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '55%',
          transform: `translate(-50%, -50%) scale(${scale})`,
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${COLORS.glow}, transparent)`,
          border: `2px solid ${COLORS.primary}`,
          boxShadow: `
            0 0 30px ${COLORS.glow},
            inset 0 0 30px ${COLORS.glow}
          `,
          opacity: 0.6 * opacity,
        }}
      />

      {/* ================================== */}
      {/* 顶部标题 */}
      {/* ================================== */}
      <div
        style={{
          position: 'absolute',
          top: 200,
          left: 0,
          right: 0,
          textAlign: 'center',
          transform: `translateY(${(1 - titleY) * 50}px)`,
          opacity: titleY,
        }}
      >
        <h1
          style={{
            fontFamily: 'Arial, sans-serif',
            fontSize: 80,
            fontWeight: 'bold',
            color: COLORS.primary,
            textShadow: `
              0 0 20px ${COLORS.glow},
              0 0 40px ${COLORS.glow}
            `,
            letterSpacing: 10,
          }}
        >
          HOLOGRAM
        </h1>
        <p
          style={{
            fontFamily: 'Arial, sans-serif',
            fontSize: 30,
            color: COLORS.accent,
            marginTop: 10,
            opacity: 0.8,
            letterSpacing: 5,
          }}
        >
          3D PROJECTION
        </p>
      </div>

      {/* ================================== */}
      {/* 底部信息 */}
      {/* ================================== */}
      <div
        style={{
          position: 'absolute',
          bottom: 200,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: opacity * 0.6,
        }}
      >
        <p
          style={{
            fontFamily: 'monospace',
            fontSize: 26,
            color: COLORS.primary,
            letterSpacing: 3,
          }}
        >
          [AR INTERFACE READY]
        </p>
        <p
          style={{
            fontFamily: 'monospace',
            fontSize: 26,
            color: COLORS.secondary,
            marginTop: 5,
          }}
        >
          RESOLUTION: 1920x1080 | FPS: 30 | FORMAT: MP4
        </p>
      </div>

      {/* ================================== */}
      {/* 装饰线条 */}
      {/* ================================== */}
      <svg
        width="100%"
        height="100%"
        style={{ position: 'absolute', pointerEvents: 'none' }}
      >
        {/* 左上角装饰 */}
        <path
          d="M 100 100 L 200 100 M 100 100 L 100 200"
          stroke={COLORS.primary}
          strokeWidth="2"
          opacity={0.5 * opacity}
        />

        {/* 右上角装饰 */}
        <path
          d="M 980 100 L 880 100 M 980 100 L 980 200"
          stroke={COLORS.primary}
          strokeWidth="2"
          opacity={0.5 * opacity}
        />

        {/* 左下角装饰 */}
        <path
          d="M 100 1820 L 200 1820 M 100 1820 L 100 1720"
          stroke={COLORS.primary}
          strokeWidth="2"
          opacity={0.5 * opacity}
        />

        {/* 右下角装饰 */}
        <path
          d="M 980 1820 L 880 1820 M 980 1820 L 980 1720"
          stroke={COLORS.primary}
          strokeWidth="2"
          opacity={0.5 * opacity}
        />
      </svg>
    </AbsoluteFill>
  );
};
